import { useMemo } from 'react';
import { VisibleTimeRange } from '../lib/visible-time-range';
import { useFilteredStationIds } from './useFilteredStationIds';
import { useEffectiveVisibleRange } from './useSyncViewStore';
import { StreamQualityReportResponseDto } from '../stream-quality.types';

export type StationFilterModel = {
  filteredStationIds: string[];
  reportHasStations: boolean;
  /** True when the report lists stations but the chip filter excludes all of them. */
  stationFilterExcludesAll: boolean;
};

/**
 * Station chip filter + derived flags only (no brush/visible-range subscription).
 * Use in layout shells (e.g. `ReportResult`, `TimelineFilterHeader`) so brush drags do not re-render them.
 */
export function useStationFilterModel(report: StreamQualityReportResponseDto): StationFilterModel {
  const filteredStationIds = useFilteredStationIds(report);
  const reportHasStations = report.data.stationIds.length > 0;
  const stationFilterExcludesAll = reportHasStations && filteredStationIds.length === 0;

  return {
    filteredStationIds,
    reportHasStations,
    stationFilterExcludesAll,
  };
}

/**
 * Full report window from `data.from` / `data.to` (no store subscription).
 */
export function useReportFullDomain(report: StreamQualityReportResponseDto): VisibleTimeRange {
  return useMemo(
    () => ({
      from: new Date(report.data.from),
      to: new Date(report.data.to),
    }),
    [report.data.from, report.data.to],
  );
}

export type ViewModel = {
  filteredStationIds: string[];
  visibleRange: VisibleTimeRange;
};

/**
 * Filter subset plus brush-linked visible window for charts, tables, and per-group axes.
 */
export function useViewModel(report: StreamQualityReportResponseDto): ViewModel {
  const filteredStationIds = useFilteredStationIds(report);
  const visibleRange = useEffectiveVisibleRange(report);

  return {
    filteredStationIds,
    visibleRange,
  };
}
